import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { Button, Icon, Card, Select, SelectInline } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'
import TaskRow from './TaskRow'

const Drafts = ({ orderId, orderTasks, companyCurrency = 'EUR', setCallback, saving }) => {
  const { formatMessage } = useIntl()
  const [languageOptions] = useApi(`/rest/v2/language?pageSize=999`)
  const [taskTypeOptions] = useApi(`/rest/v2/ordertasktype?pageSize=999`)
  const [mediaOptions] = useApi(`/rest/v2/ordertargetmedia?search[hidden]=0&search[deleted]=0&pageSize=999`)
  const [territoryMarketOptions] = useApi(`/rest/v2/territoryMarket?search[deleted]=0&pageSize=999`)
  const [usageOptions] = useApi(`/rest/v2/orderusage?pageSize=999`)
  const [bulkAddTask, setBulkAddTask] = useState(null)
  const [versions, setVersions] = useState(!!orderTasks.length ? orderTasks[0]?.versions : [])
  const emptyTask = {
    preferredSupplier: null,
    cost: 0,
    costcurrency: 'EUR',
    price: 0,
    pricecurrency: companyCurrency,
    type: 2,
    discount: 0,
    focus: true
  }
  const emptyVersion = {
    targetlanguageId: null,
    focus: true,
    tasks: [
      emptyTask
    ]
  }

  const onTaskUpdate = (versionIndex, taskIndex, key, change) => {
    const nextVersions = [...versions.map((version) => ({ ...version, tasks: [...version.tasks] }))]
    const nextTasks = [...nextVersions[versionIndex].tasks]
    nextTasks[taskIndex] = {
      ...nextTasks[taskIndex],
      [key]: change,
    }
    nextVersions[versionIndex] = { ...nextVersions[versionIndex], tasks: nextTasks }
    setVersions(nextVersions)
  }
  
  const onBulkAddTaskUpdate = (versionIndex, taskIndex, key, change) => {
    setBulkAddTask((task) => ({...task, [key]: change}))
  }
  
  const onVersionUpdate = (versionIndex, key, change) => {
    const nextVersions = [...versions]
    nextVersions[versionIndex] = {
      ...nextVersions[versionIndex],
      [key]: change,
    }
    setVersions(nextVersions)
  }

  useEffect(() => setCallback(() => {
    fetch(`/rest/v2/task/task-tree`, {
      method: 'POST',
      body: JSON.stringify({
        versions: versions,
        orderId: orderId
      })
    })
    .then((response) => response.json())
    .then(({success, data, errors}) => {
      if (!success) {
        window.alert(errors.join(', '))
      } else {
        setVersions(data?.versions ? data?.versions : [])
      }
    })
    .catch((error) => {
      console.error(error)
    })
  }), [versions])

  const onVersionAdd = (language) => {
    let nextVersions = [...versions]
    let unFocusedVersions = nextVersions.map(version => {
      let unFocusedTasks = version.tasks.map(task => ({ ...task, focus: false }));
      return { ...version, focus: false, tasks: unFocusedTasks };
    });

    setVersions([...unFocusedVersions, {...emptyVersion, targetlanguageId: language.value}])
  }
  
  const addTaskToVersion = (versionIndex) => {
    let nextVersions = [...versions]
    let unFocusedVersions = nextVersions.map(version => {
      let unFocusedTasks = version.tasks.map(task => ({ ...task, focus: false }));
      return { ...version, tasks: unFocusedTasks };
    });
    unFocusedVersions[versionIndex].tasks.push(emptyTask)
    setVersions(unFocusedVersions)
  }

  const onTaskDelete = (versionIndex, taskIndex) => {
    const nextVersions = [...versions.map((version) => ({ ...version, tasks: [...version.tasks] }))]
    const nextTasks = [...nextVersions[versionIndex].tasks]
    nextTasks.splice(taskIndex, 1)
    nextVersions[versionIndex] = { ...nextVersions[versionIndex], tasks: nextTasks }
    setVersions(nextVersions)
  }
  
  const onVersionDelete = (versionIndex) => {
    const nextVersions = [...versions]
    nextVersions.splice(versionIndex, 1)
    setVersions(nextVersions)
  }

  const getLanguageValue = (languageId) => {
    const foundLanguage = languageOptions ? languageOptions.find(({uid}) => uid == languageId) : null
    if (foundLanguage) {
      return {value: foundLanguage?.uid, label: foundLanguage?.name}
    }
    return null
  }
  
  const onBulkAdd = () => {
    const updatedVersions = versions.map(version => ({
      ...version,
      tasks: [...version.tasks, bulkAddTask]
    }))
    setBulkAddTask(null)
    setVersions(updatedVersions)
  }

  return (
    <div className="w-full space-y-4 text-sm">
      <div className="text-gray-500 mb-2 text-base"><FormattedMessage {...messages.title} /></div>
      
      {versions.map(({ targetlanguageId, tasks}, versionIndex) => 
        <Card className="bg-gray-50 p-4">
          <div className='ml-2 flex justify-between my-2'>
            <div className='w-1/3'>
              <SelectInline
                className="border border-gray-200 rounded bg-white"
                value={getLanguageValue(targetlanguageId)}
                onChange={(language) => onVersionUpdate(versionIndex, 'targetlanguageId', language?.value)} 
                options={languageOptions?.map(({ uid, name }) => ({ value: uid, label: name })) }
              />
            </div>
            <Icon
              onClick={() => !!tasks.length ? undefined : onVersionDelete(versionIndex)}
              className={`${!!tasks.length ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 cursor-pointer'} border-none`}
              icon={['fa', 'times']}
            />
          </div>
          { !!tasks.length && tasks.some(task => task.pricecurrency !== companyCurrency) &&
            <div className="p-4 border border-yellow-600 bg-yellow-300 rounded-sm text-yellow-600 my-2">
              <FormattedMessage {...messages.wrongPriceCurrency} /> {companyCurrency}
            </div>
          }
          <div className="w-full flex items-center space-x-1 px-2 text-gray-500">
            <div className="w-2/5 py-2"><FormattedMessage {...messages.supplierTitle} /> (★ = Voice Star · <Icon icon={['fas', 'funnel-dollar']} className="fa-fw text-xs" /> = Custom)</div>
            <div className="w-1/5 px-2"><FormattedMessage {...messages.taskTypeTitle} /></div>
            <div className=" w-2/5 space-x-1 px-2 flex justify-between items-center">
              <div><FormattedMessage {...messages.costTitle} /></div>
              <div><FormattedMessage {...messages.priceTitle} /></div>
              <div><FormattedMessage {...messages.discountTitle} /></div>
            </div>
            <div className="w-1/8 px-2"></div>
          </div>
          {!!tasks.length && tasks.map((task, taskIndex) => (
            <div className='my-2'>
              <TaskRow
                task={task}
                taskTypeOptions={taskTypeOptions}
                onTaskUpdate={onTaskUpdate}
                onTaskDelete={onTaskDelete}
                versionIndex={versionIndex}
                taskIndex={taskIndex}
                versionLanguageId={targetlanguageId}
                saving={saving}
                focus={task.focus}
                mediaOptions={mediaOptions}
                territoryMarketOptions={territoryMarketOptions}
                usageOptions={usageOptions}
              />
            </div>
          ))}
          <Button 
            onClick={() => addTaskToVersion(versionIndex)}
            icon={['fa', 'plus']}
            className="ml-2"
            iconPosition="right"
            ize="sm"
            variant="inline"
          >
            <FormattedMessage {...messages.addTask} />
          </Button>
        </Card>
      )}

      {bulkAddTask &&
        <Card className="bg-gray-50 p-4">
          <div className="py-2">
            <FormattedMessage {...messages.bulkAddTaskTitle} />
          </div>
          <TaskRow
            taskTypeOptions={taskTypeOptions}
            onTaskUpdate={onBulkAddTaskUpdate}
            versionIndex={0}
            taskIndex={0}
            versionLanguageId={null}
            saving={saving}
            bulkAddTask={bulkAddTask}
            mediaOptions={mediaOptions}
            territoryMarketOptions={territoryMarketOptions}
            usageOptions={usageOptions}
          />
          <div className='flex gap-2 mt-2 justify-end'>
            <Button 
              onClick={() => onBulkAdd()}
              icon={['fa', 'plus']}
              iconPosition="right"
            >
              <FormattedMessage {...messages.bulkAddAdd} />
            </Button>
            <Button 
              onClick={() => setBulkAddTask(null)}
              icon={['fa', 'plus']}
              iconPosition="right"
              state="negative"
              variant="outline"
            >
              <FormattedMessage {...messages.bulkAddCancel} />
            </Button>
          </div>
          
        </Card>
      }

      <div className="flex justify-start items-center space-x-4 w-full">
        <div className='w-1/3'>
          <Select
            required
            label={formatMessage(messages.addVersionLabel)}
            description={formatMessage(messages.addVersionDescription)}
            className="border border-gray-200 rounded bg-white w-full"
            onChange={(language) => onVersionAdd(language)} 
            options={languageOptions?.map(({ uid, name }) => ({ value: uid, label: name })) }
          />
        </div>
        <Button 
          onClick={() => setBulkAddTask(emptyTask)}
          icon={['fa', 'plus']}
          iconPosition="right"
          className="mt-10"
          disabled={!(!!versions.length)}
        >
          <FormattedMessage {...messages.bulkAddTaskButton} />
        </Button>
        { saving &&
          <div><FormattedMessage {...messages.saving} /></div>
        }
      </div>
    </div>
  )
}

export default Drafts
